import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AgxToast, useWindowSize, Breakpoints } from '@urbanx/agx-ui-components'
import { LicenceDetailsState } from '@urbanx/agx.agencies.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgxButton,
  AgxTypography,
  AgxColumn,
  AgxRow,
  AgxTextInput,
  Icon_Arrow,
} from '@urbanx/agx.ui-components2'
import { FormPrompt } from 'components/FormPrompt'
import { MenuTitles, SettingsMenu } from 'constants/menu'

import { ContentLayout, PageLayout } from 'layout'
import { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  GetListingAgreementSettings,
  UpdateListingAgreementSettings,
} from 'services'
import {
  CustomEmailParagraph,
  ListingAgreementSettings,
  NotificationTemplates,
} from 'types/agencySettings'
import { AgxToastState } from 'types/commonTypes'
import CustomParagraphModal from '../custom-paragraph/CustomParagraphModal'
import CustomParagraphSection from 'pages/custom-paragraph/CustomParagraphSection'
import { useSelectedAgency } from 'hooks'

const ListingAgreementView = () => {
  const { userAccount, getAuthToken } = useContext(AuthContext)
  const { agencyId } = useParams()
  const windowSize = useWindowSize()
  const [formLoaded, setFormLoaded] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const queryClient = useQueryClient()
  const { agency: selectedAgency } = useSelectedAgency()

  const [modalOpenForState, setModalOpenForState] =
    useState<LicenceDetailsState | null>(null)
  const [tertiaryActiveMenu, setTertiaryActiveMenu] = useState<
    MenuTitles | undefined
  >(MenuTitles.LISTING_AGREEMENT)
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })
  const [listingAgreementSettings, setListingAgreementSettings] =
    useState<ListingAgreementSettings>({
      privacyPolicyLink: '',
      customEmailParagraphs: [],
    })

  const {
    data: agencyListingAgreementSettings,
    isFetched,
    isLoading,
  } = useQuery<ListingAgreementSettings | undefined>({
    queryKey: ['getListingAgreementSettings', agencyId],
    queryFn: (ctx) =>
      GetListingAgreementSettings(getAuthToken, ctx.queryKey[1] as string),
    enabled: !!agencyId,
  })

  const { mutate: updateListingAgreementSettings } = useMutation({
    mutationFn: UpdateListingAgreementSettings,
    onSuccess: () => {
      setIsFormDirty(false)
      queryClient.invalidateQueries({
        queryKey: ['getListingAgreementSettings', agencyId],
      })
      updateToastState({
        color: 'success',
        message: 'Listing agreement saved successfully',
        open: true,
      })
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error saving listing agreement',
        open: true,
      })
    },
  })

  useEffect(() => {
    if (isFetched && agencyListingAgreementSettings) {
      setListingAgreementSettings(agencyListingAgreementSettings)
      setIsFormDirty(false)

      setFormLoaded(true)
    }
  }, [isFetched, agencyListingAgreementSettings])

  const saveChanges = useCallback(() => {
    updateListingAgreementSettings({
      getAuthToken,
      ...listingAgreementSettings,
      AgencyId: agencyId || '',
    })
  }, [
    agencyId,
    listingAgreementSettings,
    getAuthToken,
    updateListingAgreementSettings,
  ])

  const addCustomParagraph = useCallback(
    (state: LicenceDetailsState | undefined) => {
      if (!state) return
      setModalOpenForState(state)
    },
    []
  )

  const handleSaveCustomParagraph = useCallback(
    (customEmailParagraph: CustomEmailParagraph) => {
      const existingParagraphs =
        listingAgreementSettings.customEmailParagraphs || []
      const existingIndex = existingParagraphs.findIndex(
        (cp) => cp.state === customEmailParagraph.state
      )

      let updatedParagraphs
      if (existingIndex >= 0) {
        updatedParagraphs = existingParagraphs.map((cp, index) =>
          index === existingIndex
            ? { ...cp, customParagraph: customEmailParagraph.customParagraph }
            : cp
        )
      } else {
        updatedParagraphs = [
          ...existingParagraphs,
          {
            state: customEmailParagraph.state,
            customParagraph: customEmailParagraph.customParagraph,
          },
        ]
      }

      setListingAgreementSettings({
        ...listingAgreementSettings,
        customEmailParagraphs: updatedParagraphs,
      })

      setIsFormDirty(true)
      setModalOpenForState(null)
    },
    [listingAgreementSettings]
  )

  const agencyStates = useMemo(
    () => selectedAgency?.companyDetails?.licences?.map((l) => l.state) || [],
    [selectedAgency]
  )

  if (!isFetched || isLoading || !formLoaded) return null

  const privacyPolicyComponent = (
    <div className="container100Percent">
      <AgxColumn id="privacyPolicyContainer" gap="spacing-2xl" alignItems="stretch">
        <div className="borderBottomContainer">
          <AgxRow >
            <AgxTypography id="lexical-label" type="label" size="large">
              Privacy Policy
            </AgxTypography>
          </AgxRow>
        </div>
        <AgxRow gap="spacing-2xl">
          <AgxTextInput
            id="privacyPolicyLink"
            label="Privacy Policy Link"
            stretch
            type="text"
            hideOptionalText
            defaultValue={listingAgreementSettings.privacyPolicyLink || ''}
            onInputValueChange={({ value }: { value: string }) => {
              setListingAgreementSettings((previousState) => ({
                ...previousState,
                privacyPolicyLink: value,
              }))
              setIsFormDirty(true)
            }}
          />
        </AgxRow>
      </AgxColumn>
    </div>
  )

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.SETTINGS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.SETTINGS}
        tertiaryMenu={SettingsMenu}
        tertiaryActiveMenu={tertiaryActiveMenu}
      >
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn gap="spacing-2xl" alignItems="stretch">
          <div
            className={`borderBottomContainer ${windowSize === Breakpoints.Desktop ? 'pageTitleContainerWithTertiaryMenu' : ''}`}
          >
            <AgxRow fill gap="spacing-2xl" justifyContent="space-between">
              {windowSize === Breakpoints.Desktop && (
                <AgxTypography id="settings-title" type="label" size="large">
                  {MenuTitles.SETTINGS}
                </AgxTypography>
              )}
              {windowSize !== Breakpoints.Desktop && (
                <AgxButton
                  id="btn-back"
                  leftIcon={
                    <Icon_Arrow
                      direction="left"
                      height={22}
                      width={22}
                      color="var(--text-black)"
                    />
                  }
                  variant="naked"
                  size="large"
                  text={MenuTitles.LISTING_AGREEMENT}
                  onClick={() => setTertiaryActiveMenu(undefined)}
                />
              )}
              <AgxButton
                id="btn-save-changes"
                text="Save Changes"
                variant="primary"
                size="medium"
                onClick={saveChanges}
              />
            </AgxRow>
          </div>
          {agencyStates?.includes(LicenceDetailsState.QLD) &&
            privacyPolicyComponent}
          {agencyStates?.map((state) => (
            <CustomParagraphSection
              key={`agreementEmailToVendor-${state}`}
              title={`Agreement Email to Vendor (${state})`}
              componentKey={`agreementEmailToVendor-${state}`}
              customEmailParagraph={listingAgreementSettings?.customEmailParagraphs?.find(
                (cp) => cp.state === state
              )}
              onAddCustomParagraph={() => addCustomParagraph(state)}
            />
          ))}
        </AgxColumn>
        {modalOpenForState ? (
          <CustomParagraphModal
            key={`modal-${modalOpenForState}`}
            customEmailParagraph={listingAgreementSettings?.customEmailParagraphs?.find(
              (cp) => cp.state === modalOpenForState
            )}
            title={`Agreement Email to Vendor (${modalOpenForState})`}
            initialState={modalOpenForState}
            templateName={NotificationTemplates.ListingAgreementVendorTemplate}
            saveCustomParagraph={handleSaveCustomParagraph}
            setIsDialogOpen={() => setModalOpenForState(null)}
          />
        ) : (
          <></>
        )}
      </ContentLayout>
    </PageLayout>
  )
}

export default ListingAgreementView
